import React, { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from "@mui/material"
import SecurityIcon from "@mui/icons-material/Security"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import { getAppRoles, createAppRole, updateAppRole, deleteAppRole } from "helpers/api_helper"
import {
  PERMISSION_PRESETS,
  SIDEBAR_ITEMS,
  STORE_PERMISSIONS,
  isB2BUser,
  isRetailUser,
  isAdminUser,
} from "../../constants/permissions"

const RolesTab = ({ onSnackbar }) => {
  const { t } = useTranslation()
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingRole, setEditingRole] = useState(null)
  const [formData, setFormData] = useState({ name: "", description: "", permissions: {} })
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState(0)

  const fetchRoles = useCallback(async () => {
    setLoading(true)
    try {
      const result = await getAppRoles()
      setRoles(result.resource || result || [])
    } catch (err) {
      console.error("Failed to fetch roles:", err)
      onSnackbar?.({ open: true, message: t("Failed to load roles"), severity: "error" })
    }
    setLoading(false)
  }, [onSnackbar, t])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role)
      setFormData({
        name: role.name,
        description: role.description || "",
        permissions: role.permissions || {},
      })
    } else {
      setEditingRole(null)
      setFormData({ name: "", description: "", permissions: {} })
    }
    setActiveTab(0)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setEditingRole(null)
  }

  const handleSidebarToggle = (itemId) => {
    setFormData(prev => {
      const perms = { ...prev.permissions }
      const sidebar = { ...(perms.sidebar || {}) }

      if (sidebar[itemId]) {
        delete sidebar[itemId]
      } else {
        sidebar[itemId] = true
      }

      perms.sidebar = sidebar
      return { ...prev, permissions: perms }
    })
  }

  const handleStoreToggle = (key) => {
    setFormData(prev => {
      const perms = { ...prev.permissions }
      const store = { ...(perms.store || {}) }

      if (store[key]) {
        delete store[key]
      } else {
        store[key] = true
      }

      perms.store = store
      return { ...prev, permissions: perms }
    })
  }

  const handleAllToggle = () => {
    setFormData(prev => {
      if (prev.permissions.all === true) {
        return { ...prev, permissions: {} }
      }
      return { ...prev, permissions: { all: true } }
    })
  }

  const handleApplyPreset = (presetName) => {
    const preset = PERMISSION_PRESETS[presetName]
    if (preset) {
      setFormData(prev => ({ ...prev, permissions: { ...preset } }))
    }
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      onSnackbar?.({ open: true, message: t("Role name is required"), severity: "warning" })
      return
    }
    setSaving(true)
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        permissions: formData.permissions,
      }
      if (editingRole) {
        await updateAppRole(editingRole.id, payload)
        onSnackbar?.({ open: true, message: t("Role updated"), severity: "success" })
      } else {
        await createAppRole(payload)
        onSnackbar?.({ open: true, message: t("Role created"), severity: "success" })
      }
      handleCloseDialog()
      fetchRoles()
    } catch (err) {
      console.error("Save role error:", err)
      onSnackbar?.({ open: true, message: t("Failed to save role"), severity: "error" })
    }
    setSaving(false)
  }

  const handleDelete = async (role) => {
    if (!window.confirm(t("Delete role") + ` "${role.name}"?`)) return
    try {
      await deleteAppRole(role.id)
      onSnackbar?.({ open: true, message: t("Role deleted"), severity: "success" })
      fetchRoles()
    } catch (err) {
      console.error("Delete role error:", err)
      onSnackbar?.({ open: true, message: t("Failed to delete role"), severity: "error" })
    }
  }

  const getPermSummary = (permissions) => {
    if (!permissions) return "-"
    if (permissions.all === true) return t("Full Access")
    if (isB2BUser(permissions)) return "B2B"
    if (isRetailUser(permissions)) return "Retail"
    const keys = Object.keys(permissions).filter(k => k !== "all" && k !== "type")
    if (keys.length === 0) return t("No permissions")
    return keys.join(", ")
  }

  const getRoleTypeChip = (permissions) => {
    if (!permissions) return null
    if (permissions.all === true) return <Chip label="Admin" size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
    if (isB2BUser(permissions)) return <Chip label="B2B" size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: "#dbeafe", color: "#1e40af" }} />
    if (isRetailUser(permissions)) return <Chip label="Retail" size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: "#dcfce7", color: "#166534" }} />
    return <Chip label="Custom" size="small" sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700, bgcolor: "#f3f4f6", color: "#6b7280" }} />
  }

  const isFullAdmin = formData.permissions.all === true

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#ede9fe", color: "#7c3aed" }}>
            <SecurityIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("Roles & Permissions")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Manage roles and what each role can access")}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" }, borderRadius: 2, textTransform: "none" }}
        >
          {t("Add Role")}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Role")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Type")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Permissions")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Status")}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: "#475569" }}>{t("Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map(role => (
                <TableRow key={role.id} hover>
                  <TableCell>
                    <Typography fontWeight={600} fontSize="0.875rem">{role.name}</Typography>
                    {role.description && (
                      <Typography fontSize="0.7rem" color="#9ca3af">{role.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {getRoleTypeChip(role.permissions)}
                  </TableCell>
                  <TableCell>
                    <Typography fontSize="0.75rem" color="#6b7280" sx={{ maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {getPermSummary(role.permissions)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={role.is_active ? t("Active") : t("Inactive")}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        bgcolor: role.is_active ? "#dcfce7" : "#fee2e2",
                        color: role.is_active ? "#166534" : "#991b1b",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(role)} sx={{ color: "#3b82f6" }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {role.name !== "Admin" && (
                      <IconButton size="small" onClick={() => handleDelete(role)} sx={{ color: "#ef4444" }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {roles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="#9ca3af">{t("No roles found")}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Role Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingRole ? t("Edit Role") : t("Create Role")}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label={t("Role Name")}
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              size="small"
              disabled={editingRole?.name === "Admin"}
            />
            <TextField
              label={t("Description")}
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              fullWidth
              size="small"
            />

            {/* Quick Presets */}
            <Box>
              <Typography variant="caption" color="#6b7280" fontWeight={600} sx={{ mb: 1, display: "block" }}>
                {t("Quick Presets")}
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {[
                  { key: "admin", label: "Admin", color: "#fef3c7", textColor: "#92400e" },
                  { key: "b2b", label: "B2B", color: "#dbeafe", textColor: "#1e40af" },
                  { key: "retail", label: "Retail", color: "#dcfce7", textColor: "#166534" },
                ].map(preset => (
                  <Chip
                    key={preset.key}
                    label={preset.label}
                    size="small"
                    onClick={() => handleApplyPreset(preset.key)}
                    sx={{
                      height: 26,
                      fontSize: "0.72rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      bgcolor: preset.color,
                      color: preset.textColor,
                      "&:hover": { opacity: 0.85 },
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Admin toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.permissions.all === true}
                  onChange={handleAllToggle}
                  color="primary"
                />
              }
              label={
                <Typography fontSize="0.875rem" fontWeight={500}>
                  {t("Full Admin Access")}
                </Typography>
              }
            />

            {/* Tabbed permission editor (hidden when full admin) */}
            {!isFullAdmin && (
              <Box>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{
                    minHeight: 36,
                    mb: 2,
                    "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: "0.8rem", fontWeight: 600 },
                  }}
                >
                  <Tab label={t("Sidebar Access")} />
                  <Tab label={t("Store / CPQ")} />
                </Tabs>

                {/* Tab 1: Sidebar */}
                {activeTab === 0 && (
                  <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f9fafb" }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#6b7280" }}>
                            {t("Menu Item")}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#6b7280", width: 80 }}>
                            {t("Access")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {SIDEBAR_ITEMS.map(item => (
                          <TableRow key={item.id} hover>
                            <TableCell sx={{ fontSize: "0.813rem", fontWeight: 500 }}>
                              {t(item.label)}
                            </TableCell>
                            <TableCell align="center">
                              <Switch
                                size="small"
                                checked={formData.permissions.sidebar?.[item.id] === true}
                                onChange={() => handleSidebarToggle(item.id)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Tab 2: Store / CPQ */}
                {activeTab === 1 && (
                  <TableContainer sx={{ border: "1px solid #e5e7eb", borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f9fafb" }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#6b7280" }}>
                            {t("Permission")}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, fontSize: "0.75rem", color: "#6b7280", width: 80 }}>
                            {t("Enabled")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {STORE_PERMISSIONS.map(perm => (
                          <TableRow key={perm.key} hover>
                            <TableCell sx={{ fontSize: "0.813rem", fontWeight: 500 }}>
                              {t(perm.label)}
                            </TableCell>
                            <TableCell align="center">
                              <Switch
                                size="small"
                                checked={formData.permissions.store?.[perm.key] === true}
                                onChange={() => handleStoreToggle(perm.key)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Role type selector */}
                    <Box sx={{ p: 2, borderTop: "1px solid #e5e7eb" }}>
                      <Typography variant="caption" color="#6b7280" fontWeight={600} sx={{ mb: 1, display: "block" }}>
                        {t("User Type")}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {[
                          { type: "b2b", label: "B2B Customer" },
                          { type: "retail", label: "Retail / Staff" },
                        ].map(opt => {
                          const isActive = formData.permissions.type === opt.type
                          return (
                            <Chip
                              key={opt.type}
                              label={t(opt.label)}
                              size="small"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  permissions: {
                                    ...prev.permissions,
                                    type: isActive ? undefined : opt.type,
                                  },
                                }))
                              }}
                              sx={{
                                height: 28,
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                cursor: "pointer",
                                bgcolor: isActive ? "#3b82f6" : "#f3f4f6",
                                color: isActive ? "#fff" : "#374151",
                                "&:hover": { bgcolor: isActive ? "#2563eb" : "#e5e7eb" },
                              }}
                            />
                          )
                        })}
                      </Stack>
                    </Box>
                  </TableContainer>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ textTransform: "none", color: "#6b7280" }}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ bgcolor: "#3b82f6", "&:hover": { bgcolor: "#2563eb" }, textTransform: "none" }}
          >
            {saving ? <CircularProgress size={20} /> : editingRole ? t("Update") : t("Create")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RolesTab
